import { Form } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomInput, CustomInputPassword, CustomSelect } from "sipli-fleet-core/components";
import { useQueryGetClientLookup, useQueryGetLanguageLookup } from "src/queries/lookup.queries";
import {
  useQueryGetUserClientLanguage,
  useQueryGetUserDistributorLookup,
  useQueryGetUserRoleLookup,
} from "src/queries/users.queries";
import type { UserFormValues, UserRole } from "./types";
import {
  DEFAULT_LANGUAGE_ID,
  getResponseArray,
  getResponseObject,
  getRoleName,
  isCustomerRoleValue,
  isHighLevelRole,
  isSuperAdminRoleValue,
  isSuperUserRoleValue,
  shouldRequirePassword,
  toSelectOptions,
} from "./userModalHelpers";

interface UserInfoStepProps {
  form: ReturnType<typeof Form.useForm<UserFormValues>>[0];
  open: boolean;
  isEditing: boolean;
  fallbackDistributorId?: string | number | null;
  currentRole: string;
  customerLimitErrorMessage?: string;
  onCustomerSelected?: (customerId: string | number) => void;
}

const fieldClass = "user-modal-field";
const controlClass = "!h-[32px] !rounded-[4px]";

const UserInfoStep = ({
  form,
  open,
  isEditing,
  fallbackDistributorId,
  currentRole,
  customerLimitErrorMessage,
  onCustomerSelected,
}: UserInfoStepProps) => {
  const { t } = useTranslation();
  const initialClientIdRef = useRef<string | number | undefined>(undefined);
  const [customerChanged, setCustomerChanged] = useState(false);
  const roleId = Form.useWatch("userRoleId", form);
  const distributorId = Form.useWatch("distributorId", form) ?? fallbackDistributorId;
  const clientId = Form.useWatch("clientId", form);
  const rolesQuery = useQueryGetUserRoleLookup({ enabled: open });
  const roles = useMemo(() => getResponseArray<UserRole>(rolesQuery.data), [rolesQuery.data]);
  const roleName = getRoleName(roles, roleId);
  const selectedRoleHidesDistributor =
    isSuperUserRoleValue(roleId, roleName) || isSuperAdminRoleValue(roleName);
  const showDistributorSelect = isHighLevelRole(currentRole) && !selectedRoleHidesDistributor;
  const distributorQuery = useQueryGetUserDistributorLookup({
    enabled: open && showDistributorSelect,
  });
  const languageQuery = useQueryGetLanguageLookup({}, { enabled: open });
  const clientQuery = useQueryGetClientLookup(distributorId ? { distributorId } : {}, {
    enabled: open,
  });
  const clientLanguageQuery = useQueryGetUserClientLanguage(clientId, {
    enabled:
      open &&
      Boolean(clientId) &&
      isCustomerRoleValue(
        roleId,
        getRoleName(getResponseArray<UserRole>(rolesQuery.data), roleId)
      ) &&
      (!isEditing || customerChanged),
  });

  const roleOptions = useMemo(
    () => toSelectOptions(getResponseArray<UserRole>(rolesQuery.data)),
    [rolesQuery.data]
  );
  const shouldShowCustomer = isCustomerRoleValue(roleId, roleName);
  const requiresPassword = shouldRequirePassword(isEditing, roleId);

  const distributorOptions = useMemo(
    () => toSelectOptions(getResponseArray(distributorQuery.data)),
    [distributorQuery.data]
  );
  const languageOptions = useMemo(
    () => toSelectOptions(getResponseArray(languageQuery.data)),
    [languageQuery.data]
  );
  const clientOptions = useMemo(
    () => toSelectOptions(getResponseArray(clientQuery.data)),
    [clientQuery.data]
  );

  const clientLanguage = getResponseObject<{ id?: string | number }>(clientLanguageQuery.data);

  useEffect(() => {
    if (!open) return;

    if (!isEditing) {
      initialClientIdRef.current = undefined;
      setCustomerChanged(false);
      return;
    }

    if (!customerChanged) {
      initialClientIdRef.current = clientId;
    }
  }, [clientId, customerChanged, open, isEditing]);

  useEffect(() => {
    if (isEditing && !customerChanged) return;

    if (clientLanguage?.id && form.getFieldValue("languageId") !== clientLanguage.id) {
      form.setFieldValue("languageId", clientLanguage.id);
    }
  }, [clientLanguage?.id, customerChanged, form, isEditing]);

  useEffect(() => {
    if (!requiresPassword) {
      form.setFieldsValue({ password: "", retypePassword: "" });
    }
  }, [form, requiresPassword]);

  useEffect(() => {
    if (selectedRoleHidesDistributor) {
      form.setFieldValue("distributorId", undefined);
    }
  }, [form, selectedRoleHidesDistributor]);

  return (
    <div className="user-modal-step">
      <Form form={form} layout="vertical" requiredMark={false} className="user-modal-form">
        <Form.Item
          name="username"
          label={t("username")}
          rules={[{ required: true, whitespace: true, message: t("field_required") }]}
          className={fieldClass}
        >
          <CustomInput
            className={controlClass}
            placeholder={t("enter_username")}
            autoComplete="off"
          />
        </Form.Item>

        <div className="user-modal-grid">
          <Form.Item
            name="name"
            label={t("name")}
            rules={[{ required: true, whitespace: true, message: t("name_required") }]}
            className={fieldClass}
          >
            <CustomInput className={controlClass} placeholder={t("enter_name")} />
          </Form.Item>
          <Form.Item
            name="surname"
            label={t("surname")}
            rules={[{ required: true, whitespace: true, message: t("surname_is_required") }]}
            className={fieldClass}
          >
            <CustomInput className={controlClass} placeholder={t("enter_surname")} />
          </Form.Item>
        </div>

        <Form.Item
          name="languageId"
          label={t("language")}
          rules={[{ required: true, message: t("field_required") }]}
          className={fieldClass}
          initialValue={DEFAULT_LANGUAGE_ID}
        >
          <CustomSelect
            className={controlClass}
            placeholder={t("select_language")}
            loading={languageQuery.isLoading}
            options={languageOptions}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>

        <Form.Item
          name="email"
          label={t("email")}
          rules={[
            { required: true, message: t("required_email") },
            { type: "email", message: t("invalid_email") },
          ]}
          className={fieldClass}
        >
          <CustomInput
            className={controlClass}
            placeholder={t("enter_user_email")}
            disabled={isEditing}
            autoComplete="off"
          />
        </Form.Item>

        {!isEditing && (
          <Form.Item
            name="retypeEmail"
            label={t("retype_email")}
            dependencies={["email"]}
            rules={[
              { required: true, message: t("required_retyped_email") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("email") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t("email_not_match")));
                },
              }),
            ]}
            className={fieldClass}
          >
            <CustomInput
              className={controlClass}
              placeholder={t("retype_user_email")}
              autoComplete="off"
            />
          </Form.Item>
        )}

        {requiresPassword && (
          <div className="user-modal-grid">
            <Form.Item
              name="password"
              label={t("password")}
              rules={[
                { required: true, message: t("required_password") },
                { min: 6, message: t("password_min_length") },
              ]}
              className={fieldClass}
            >
              <CustomInputPassword
                className={controlClass}
                placeholder={t("enter_password")}
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              name="retypePassword"
              label={t("retype_password")}
              dependencies={["password"]}
              rules={[
                { required: true, message: t("required_retyped_password") },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t("password_not_match")));
                  },
                }),
              ]}
              className={fieldClass}
            >
              <CustomInputPassword
                className={controlClass}
                placeholder={t("retype_password")}
                autoComplete="new-password"
              />
            </Form.Item>
          </div>
        )}

        <div className="user-modal-grid">
          <Form.Item
            name="userRoleId"
            label={t("role")}
            rules={[{ required: true, message: t("field_required") }]}
            className={fieldClass}
          >
            <CustomSelect
              className={controlClass}
              placeholder={t("select_role")}
              loading={rolesQuery.isLoading}
              options={roleOptions}
              showSearch
              optionFilterProp="label"
              onChange={() => {
                form.setFieldValue("clientId", undefined);
                setCustomerChanged(true);
              }}
            />
          </Form.Item>

          {showDistributorSelect && (
            <Form.Item
              name="distributorId"
              label={t("distributor")}
              rules={[{ required: true, message: t("field_required") }]}
              className={fieldClass}
            >
              <CustomSelect
                className={controlClass}
                placeholder={t("select_distributor")}
                loading={distributorQuery.isLoading}
                options={distributorOptions}
                showSearch
                optionFilterProp="label"
                onChange={() => {
                  form.setFieldValue("clientId", undefined);
                  setCustomerChanged(true);
                }}
              />
            </Form.Item>
          )}
        </div>

        {shouldShowCustomer && (
          <Form.Item
            name="clientId"
            label={t("customer")}
            rules={[{ required: true, message: t("field_required") }]}
            className={fieldClass}
            validateStatus={customerLimitErrorMessage ? "error" : undefined}
            help={customerLimitErrorMessage}
          >
            <CustomSelect
              className={controlClass}
              status={customerLimitErrorMessage ? "error" : undefined}
              placeholder={t("select_customer")}
              loading={clientQuery.isLoading}
              options={clientOptions}
              showSearch
              optionFilterProp="label"
              onSelect={(value) => onCustomerSelected?.(value)}
              onChange={(value) => {
                setCustomerChanged(String(value) !== String(initialClientIdRef.current));
              }}
            />
          </Form.Item>
        )}
      </Form>
    </div>
  );
};

export default UserInfoStep;
